import React, { useEffect, useState } from 'react';
import { Spin } from 'antd';
import apiClient from '../../utils/axios';

export default function ContactLifecycleView({ contactId }) {
  const [lifecycles, setLifecycles] = useState([]);
  const [definitionMap, setDefinitionMap] = useState({}); // lifecycle_id -> definition
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!contactId) return;

    let cancelled = false;
    setLoading(true);

    const load = async () => {
      try {
        // Fetch contact lifecycle states and all definitions in parallel
        const [statesRes, defsRes] = await Promise.all([
          apiClient.get(`/contacts/${contactId}/lifecycles`),
          apiClient.get('/lifecycles'),
        ]);
        if (cancelled) return;

        const states = statesRes.data || [];
        const defs = defsRes.data || [];

        // Build a map from lifecycle_id -> definition
        const defMap = {};
        defs.forEach(d => { defMap[d.id] = d; });

        setLifecycles(states);
        setDefinitionMap(defMap);
      } catch {
        // non-fatal — don't show an error for missing lifecycle data
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [contactId]);

  if (!contactId) return null;
  if (loading) return <Spin size="small" style={{ display: 'block', padding: '8px 0' }} />;
  if (lifecycles.length === 0) return (
    <p style={{ color: 'var(--text-muted)', fontSize: 12, fontStyle: 'italic', margin: 0 }}>
      No lifecycle data for this contact.
    </p>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {lifecycles.map((lc) => {
        const def = definitionMap[lc.lifecycle_id];
        const allStages = def
          ? [...def.stages].sort((a, b) => a.order - b.order)
          : buildStagesFromHistory(lc);

        // Build a map of stage_key -> history entry
        const historyMap = {};
        (lc.stage_history || []).forEach(h => { historyMap[h.stage_key] = h; });

        const currentStageKey = lc.current_stage_key;

        return (
          <div
            key={lc.lifecycle_id}
            style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: 8,
              padding: '12px 14px',
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 10 }}>
              {def?.name || lc.lifecycle_id}
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4 }}>
              {allStages.map((stage, index) => {
                const histEntry = historyMap[stage.key];
                const entered = !!histEntry;
                const isCurrent = stage.key === currentStageKey;
                const isPending = !entered;

                let dateLabel = null;
                if (entered && histEntry.entered_at) {
                  const d = new Date(histEntry.entered_at);
                  dateLabel = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
                }

                let stageStyle;
                if (isCurrent) {
                  stageStyle = {
                    background: 'rgba(22,119,255,0.08)',
                    border: '2px solid rgba(22,119,255,0.5)',
                    boxShadow: '0 0 10px rgba(22,119,255,0.1)',
                    padding: '7px 14px',
                    fontSize: 13,
                  };
                } else if (entered) {
                  stageStyle = {
                    background: 'var(--bg-tertiary)',
                    border: '1px solid var(--border-color)',
                    padding: '6px 12px',
                    fontSize: 12,
                  };
                } else {
                  stageStyle = {
                    background: 'var(--bg-secondary)',
                    border: '1px dashed var(--border-color)',
                    padding: '6px 12px',
                    fontSize: 12,
                  };
                }

                return (
                  <React.Fragment key={stage.key}>
                    <div style={{ borderRadius: 6, ...stageStyle }}>
                      <div style={{
                        fontWeight: isCurrent ? 600 : 500,
                        color: isCurrent ? '#1677ff' : isPending ? 'var(--text-muted)' : 'var(--text-secondary)',
                      }}>
                        {stage.label}
                      </div>
                      {dateLabel && (
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                          {dateLabel}
                        </div>
                      )}
                    </div>

                    {index < allStages.length - 1 && (
                      <span style={{
                        color: !historyMap[allStages[index + 1]?.key]
                          ? 'var(--border-color)'
                          : 'var(--text-muted)',
                        fontSize: 12,
                        userSelect: 'none',
                      }}>→</span>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Fallback: reconstruct stage list from stage_history when lifecycle definition unavailable.
// Uses index-based order throughout so stage.order is consistent with currentOrder comparisons.
function buildStagesFromHistory(lc) {
  const history = lc.stage_history || [];
  const stages = history.map((h, i) => ({ key: h.stage_key, label: h.stage_key, order: i }));
  if (lc.current_stage_key && !stages.find(s => s.key === lc.current_stage_key)) {
    // Append current stage after all history entries (history entries are 0-indexed).
    stages.push({ key: lc.current_stage_key, label: lc.current_stage_key, order: history.length });
  }
  return stages.sort((a, b) => a.order - b.order);
}
